import {useCallback, useEffect, useState} from 'react';

const VIEWPORT_MARGIN = 12;

/**
 * How long to keep re-measuring after a panel opens. Must outlast the
 * sidebar's width transition: a popover opened while the rail is expanding is
 * anchored to an element that moves for the whole animation, and measuring
 * once would strand it at the rail's old edge.
 */
const TRACK_DURATION_MS = 350;

/**
 * Tracks fixed-position coordinates for a portalled panel anchored to the
 * right edge of `anchorRef`.
 *
 * Sidebar popovers have to be portalled: the nav list scrolls vertically, and
 * an absolutely positioned child would be clipped by that scroll container the
 * moment it extended past the sidebar's edge.
 *
 * @param {object} anchorRef Ref to the element the panel points at.
 * @param {boolean} open Only measures while open.
 * @param {number} gap Horizontal gap between anchor and panel, in px.
 * @returns {{top: number, left: number}|null} Coordinates, or null before the
 *   first measurement.
 */
const usePortalPosition = (anchorRef, open, gap = 8) => {
  const [position, setPosition] = useState(null);

  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const next = {top: rect.top, left: rect.right + gap};
    // Bail out when nothing moved, so the tracking loop below doesn't cause a
    // re-render on every frame.
    setPosition((prev) => (
      prev && prev.top === next.top && prev.left === next.left ? prev : next
    ));
  }, [anchorRef, gap]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    let frame = null;
    let elapsed = 0;
    const tick = () => {
      measure();
      elapsed += 16;
      if (elapsed < TRACK_DURATION_MS) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    // `true` captures scrolls inside the sidebar's own scroll container, not
    // just the window.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  return position;
};

/**
 * Clamps a measured `top` so a panel of `height` stays inside the viewport.
 */
export const clampTop = (top, height) => {
  if (!height) return top;
  const maxTop = window.innerHeight - height - VIEWPORT_MARGIN;
  return Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));
};

export default usePortalPosition;
